import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogIn, LogOut as LogOutIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return <p className="font-display text-6xl font-black md:text-8xl" data-testid="live-clock">{now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Kolkata" })}</p>;
};

export const TodayCard = ({ onChange }) => {
  const [today, setToday] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/me/today").then((r) => setToday(r.data)).catch(() => setToday({ date: "", record: null })), []);
  useEffect(() => {
    load();
    return undefined;
  }, [load]);

  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/me/${kind}`);
      toast.success(kind === "check-in" ? "Checked in. Have a good shift!" : "Checked out. See you tomorrow.");
      await load();
      onChange();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const rec = today?.record;
  const dateText = today?.date ? new Date(`${today.date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) : "";

  return (
    <section className="clip-notch relative overflow-hidden bg-[var(--ink-2)] p-6 md:p-10" data-testid="today-card">
      <p className="overline text-[var(--signal)]">Today · {dateText}</p>
      <Clock />
      <div className="mt-6 grid grid-cols-2 gap-px bg-white/10 sm:max-w-md">
        <div className="bg-[var(--ink-2)] p-4"><p className="overline text-[var(--bone-2)]">Check in</p><p className="mt-1 font-mono text-2xl" data-testid="today-check-in">{rec?.check_in || "--:--"}</p></div>
        <div className="bg-[var(--ink-2)] p-4"><p className="overline text-[var(--bone-2)]">Check out</p><p className="mt-1 font-mono text-2xl" data-testid="today-check-out">{rec?.check_out || "--:--"}</p></div>
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        {!rec?.check_in && (
          <button onClick={() => act("check-in")} disabled={busy || !today} data-testid="check-in-button"
            className="flex items-center gap-2 rounded-full bg-[var(--signal)] px-7 py-4 font-semibold text-[var(--ink)] transition-transform hover:-translate-y-0.5 disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogIn className="h-5 w-5" />} Mark attendance — Check in
          </button>
        )}
        {rec?.check_in && !rec?.check_out && (
          <button onClick={() => act("check-out")} disabled={busy} data-testid="check-out-button"
            className="flex items-center gap-2 rounded-full border-2 border-[var(--signal)] px-7 py-4 font-semibold text-[var(--signal)] transition-colors hover:bg-[var(--signal)] hover:text-[var(--ink)] disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <LogOutIcon className="h-5 w-5" />} Check out
          </button>
        )}
        {rec?.check_out && <p className="text-[var(--bone-2)]" data-testid="today-done">Attendance complete for today.</p>}
      </div>
    </section>
  );
};
